import { listHeaderStyles } from "../styles/shared/listHeaderStyles";
// src/components/TopListHeaderBar.jsx
import React from "react";
import {
  Box,
  CardHeader,
  Typography,
  TextField,
  Button,
  InputAdornment } from "@mui/material";
import FigmaIcon, { getFigmaActionIcon } from "./icons/FigmaIcon";
import {
  withPillButtonClassName } from "../utils/pillButtonStyles";

/**
 * actions: array of button configs
 * [
 *   {
 *     key: "filter",
 *     label: "Filter by",
 *     variant: "outlined" | "contained" | "text",
 *     startIcon: <SomeIcon />,
 *     onClick: () => {},
 *     sx: { ... },           // optional extra styles
 *     size: "small" | "medium" | "large",
 *     disabled: bool,
 *   },
 *   ...
 * ]
 *
 * Props added for tab badge (like screenshot):
 *  - tabLabel: string (e.g., "Leads", "Jobs")
 *  - tabCount: number|string (e.g., 3247)
 *  - showTab: boolean (default true when tabLabel exists)
 */
const TopListHeaderBar = ({
  title,
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  searchIcon,
  actions = [],
  tabLabel,
  tabCount,
  showTab,
  resultsToolbarTargetId,
  tabContent }) => {
  const SHOW_TAB = Boolean(showTab ?? tabLabel);

  return (
    <Box
      mb={1}
      className="side-padding-16px table-view-header"
      sx={listHeaderStyles.root()}
    >
      {/* Top row (title + right controls) */}
      <Box
        sx={listHeaderStyles.headingRow()}
      >
        <CardHeader
          className="topbar-title"
          title={<Typography variant="h6">{title}</Typography>}
          sx={listHeaderStyles.title()}
        />

        <Box
          className="sm-flex"
          sx={listHeaderStyles.controls()}
        >
          <TextField
            size="small"
            className="top-search-input"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={onSearchChange}
            sx={listHeaderStyles.search()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searchIcon || <FigmaIcon name="search" />}
                </InputAdornment>
              ) }}
          />

          <Box
            className="inner-top-btns"
            sx={listHeaderStyles.actions()}
          >
            {actions.map((action) => (
              <Button
                key={action.key || action.label}
                className={withPillButtonClassName(action.className)}
                variant={action.variant || "outlined"}
                size={action.size || "small"}
                startIcon={(action.startIcon ?? action.icon) ? (getFigmaActionIcon(action.label) ?? action.startIcon ?? action.icon) : (action.startIcon ?? action.icon)}
                onClick={action.onClick}
                disabled={action.disabled}
                sx={listHeaderStyles.actionButton({ action })}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ✅ Tab row: ONLY the grey baseline is full width.
          The blue underline stays only under the tab label+count (like screenshot). */}
      {tabContent ? <Box sx={listHeaderStyles.customTabRow()}>{tabContent}</Box> : null}
      {resultsToolbarTargetId ? <Box id={resultsToolbarTargetId} /> : null}

      {SHOW_TAB && (
        <Box sx={listHeaderStyles.tabRow()}>
          {/* Tab content (shrink to content width) */}
          <Box
            sx={listHeaderStyles.tabContent()}
          >
            <Typography
              sx={listHeaderStyles.tabLabel()}
            >
              {tabLabel}
            </Typography>

            {tabCount !== undefined && tabCount !== null && (
              <Box
                sx={listHeaderStyles.tabCount()}
              >
                {tabCount}
              </Box>
            )}

            {/* Blue active underline (ONLY under the tab content) */}
            <Box
              sx={listHeaderStyles.tabIndicator()}
            />
          </Box>

          {/* Grey baseline FULL WIDTH */}
          <Box
            sx={listHeaderStyles.baseline()}
          />
        </Box>
      )}
    </Box>
  );
};

export default TopListHeaderBar;
